import React, { createContext, useContext, useState, useEffect } from 'react';
import LocationService from '../services/locationService';

const LocationContext = createContext();

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};

export const LocationProvider = ({ children }) => {
  const [currentLocation, setCurrentLocation] = useState(null);
  const [availableServices, setAvailableServices] = useState({
    doctors: true,
    pharmacy: true,
    lab: true,
    homeCollection: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Load location from localStorage on app start
  useEffect(() => {
    const savedLocation = localStorage.getItem('geeth365_location');
    if (savedLocation) {
      const location = JSON.parse(savedLocation);
      setCurrentLocation(location);
      updateServiceAvailability(location.pincode);
    } else {
      // Set default location
      setCurrentLocation({
        pincode: '500034',
        city: 'Hyderabad',
        state: 'Telangana',
        area: 'Banjara Hills',
        formattedAddress: 'Banjara Hills, Hyderabad, Telangana 500034'
      });
    }
  }, []);

  const updateLocation = async (pincode) => {
    setLoading(true);
    setError('');
    
    try {
      const locationData = await LocationService.getLocationFromPincode(pincode);
      const newLocation = {
        pincode: locationData.pincode,
        city: locationData.city,
        state: locationData.state,
        area: locationData.locality,
        formattedAddress: locationData.formattedAddress
      };
      
      setCurrentLocation(newLocation);
      localStorage.setItem('geeth365_location', JSON.stringify(newLocation));
      updateServiceAvailability(pincode);
      
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateLocationByGPS = async () => {
    setLoading(true);
    setError('');
    
    try {
      const position = await LocationService.getCurrentLocation();
      const address = await LocationService.reverseGeocode(position.lat, position.lng);
      
      const newLocation = {
        pincode: address.pincode,
        city: address.city,
        state: address.state,
        area: address.locality,
        formattedAddress: address.formattedAddress,
        coordinates: { lat: position.lat, lng: position.lng }
      };
      
      setCurrentLocation(newLocation);
      localStorage.setItem('geeth365_location', JSON.stringify(newLocation));
      updateServiceAvailability(address.pincode);
      
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Determine service availability based on pincode
  const updateServiceAvailability = (pincode) => {
    const firstTwoDigits = pincode.substring(0, 2);
    
    // Service coverage mapping
    const serviceCoverage = {
      // Telangana - Full services
      '50': {
        doctors: true,
        pharmacy: true,
        lab: true,
        homeCollection: true,
        deliveryTime: '2-4 hours',
        labCollection: 'Same day'
      },
      // Andhra Pradesh - Full services
      '51': {
        doctors: true,
        pharmacy: true,
        lab: true,
        homeCollection: true,
        deliveryTime: '4-6 hours',
        labCollection: 'Same day'
      },
      // Karnataka - Full services
      '56': {
        doctors: true,
        pharmacy: true,
        lab: true,
        homeCollection: true,
        deliveryTime: '6-8 hours',
        labCollection: 'Next day'
      },
      // Tamil Nadu - Full services
      '60': {
        doctors: true,
        pharmacy: true,
        lab: true,
        homeCollection: false,
        deliveryTime: '8-12 hours',
        labCollection: 'Next day'
      },
      // Maharashtra - Limited services
      '40': {
        doctors: true,
        pharmacy: true,
        lab: false,
        homeCollection: false,
        deliveryTime: '12-24 hours',
        labCollection: 'Not available'
      },
      // Delhi NCR - Full services
      '11': {
        doctors: true,
        pharmacy: true,
        lab: true,
        homeCollection: true,
        deliveryTime: '4-6 hours',
        labCollection: 'Same day'
      }
    };

    const coverage = serviceCoverage[firstTwoDigits] || {
      doctors: true,
      pharmacy: false,
      lab: false,
      homeCollection: false,
      deliveryTime: 'Contact support',
      labCollection: 'Not available'
    };

    setAvailableServices(coverage);
  };

  const getServiceMessage = () => {
    if (!currentLocation) return [];
    
    const messages = [];
    
    if (availableServices.doctors) {
      messages.push('👨‍⚕️ Doctor consultations available');
    }
    
    if (availableServices.pharmacy) {
      messages.push(`💊 Medicine delivery in ${availableServices.deliveryTime || '2-4 hours'}`);
    }
    
    if (availableServices.lab) {
      messages.push(`🧪 Lab tests with ${availableServices.homeCollection ? 'home collection' : 'center visit'}`);
    }

    if (!availableServices.pharmacy && !availableServices.lab) {
      messages.push('📞 Call 7337541113 for service availability');
    }

    return messages; // Always return array
  };

  const isServiceAvailable = (service) => {
    return availableServices[service] || false;
  };

  const value = {
    currentLocation,
    availableServices,
    loading,
    error,
    updateLocation,
    updateLocationByGPS,
    getServiceMessage,
    isServiceAvailable
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

export default LocationContext;